<template>
  <div class="post-preview-section-skeleton">
    <div class="post-preview-section">
      <ul class="post-preview-section__tabs-row">
        <!-- Tab with logo -->
        <li
          :class="{
            'tab-with-logo': true,
            'tab-with-logo--active': socialNetwork === 'Facebook',
            'post-preview-section__tab-with-logo': true,
          }"
          v-for="socialNetwork in ['Facebook', 'Instagram']"
          :key="socialNetwork"
        >
          <IconEmbedded
            v-if="socialNetwork === 'Facebook'"
            name="facebook"
            :class="{
              'tab-with-logo__logo': true,
              'tab-with-logo__logo--no-right-margin': ['mobile'].includes(screenSize),
            }"
            color="rgba(59, 89, 152, 1)"
          />
          <IconEmbedded
            v-else-if="socialNetwork === 'Instagram'"
            name="instagram"
            :class="{
              'tab-with-logo__logo': true,
              'tab-with-logo__logo--no-right-margin': ['mobile'].includes(screenSize),
              'tab-with-logo__logo--instagram': true,
            }"
            color="rgba(255, 255, 255, 1)"
          />

          <div v-if="!['mobile'].includes(screenSize)" class="tab-with-logo__text">{{ socialNetwork }}</div>
        </li>
        <!-- / Tab with logo -->
      </ul>

      <div class="post-preview-section__post-snippet-wrap">
        <div class="post-preview-section__slot-wrap">
          <slot />
        </div>

        <div class="post-preview-section__snippet">
          <span class="post-preview-section__snippet-text">
            Escape to adventure in Visit Funtown, where every sunset paints the sky and the water awaits your paddle. Discover tranquility, capture the moment, and feel the thrill of nature's beauty.
            Your unforgettable journey starts here.
          </span>

          <div class="photos-section">
            <div class="photos-section__photo photos-section__photo--big"></div>
            <div class="photos-section__photo photos-section__photo--big"></div>
            <div class="photos-section__photo photos-section__photo--small"></div>
            <div class="photos-section__photo photos-section__photo--small"></div>
            <div class="photos-section__photo photos-section__photo--small"></div>
          </div>

          <div class="buttons-panel">
            <div class="buttons-panel__button buttons-panel__button--big"></div>
            <div class="buttons-panel__button buttons-panel__button--small"></div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { inject, onBeforeMount, Ref } from "vue";

// Components
import IconEmbedded from "@components/ui/IconEmbedded.vue";

// Types
import { ScreenSize } from "@contracts/screenSize";

// Global variables & injections
const screenSize = inject("screenSize") as Ref<ScreenSize>;

const props = withDefaults(
  defineProps<{
    isWithShareAndDislikeButtons?: boolean;
  }>(),
  {
    isWithShareAndDislikeButtons: false,
  }
);
</script>

<style scoped lang="scss">
@import "@/scss/mixins/animations/skeleton-loading.scss";
@import "@/scss/mixins/animations/loading-bg.scss";

// Tab with logo ==============================================================
.tab-with-logo {
  height: 42px;
  padding: 0 11px 2px 11px;
  box-sizing: border-box;
  border: 1px #d9d9d9 solid;
  border-width: 1px 1px 0 1px;
  border-radius: 6px 6px 0 0;
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
  font:
    700 15px/15px "Quicksand",
    sans-serif;
  background: #d9d9d9;
  cursor: pointer;
  user-select: none;

  &--active {
    background: #fff;
  }

  &__logo {
    width: 26px;
    height: 26px;
    margin-right: 3px;
    display: flex;
    justify-content: center;
    align-items: center;
    font-size: 27px;
    line-height: 27px;
    text-align: center;

    &--instagram {
      width: 20px;
      height: 20px;
      margin-right: 6px;
      border-radius: 5px;
      color: #fff;
      font-size: 18px;
      line-height: 18px;
      background: radial-gradient(circle at 30% 107%, #fdf497 0%, #fdf497 5%, #fd5949 45%, #d6249f 60%, #285aeb 90%);
    }

    &--tiktok {
      width: 20px;
      height: 20px;
      padding: 1px 0 0 1px;
      box-sizing: border-box;
      margin-right: 6px;
      border-radius: 5px;
      color: #fff;
      font-size: 12px;
      line-height: 12px;
      background: black;

      &::before {
        filter: drop-shadow(1px 0px 0px #fd3e3e) drop-shadow(-1px -1px 0px #4de8f4);
      }
    }

    &--no-right-margin {
      margin-right: 0;
    }
  }

  &__text {
  }
}

// Share and dislike buttons ==================================================
.share-and-dislike-buttons {
  gap: 12px;
  display: flex;
  justify-content: space-between;

  &__share-button {
  }

  &__dislike-button {
    width: 50px;
  }
}

// Post preview section =======================================================
.post-preview-section {
  display: flex;
  flex-direction: column;

  &__tabs-row {
    padding: 0;
    margin: 0 0 -1px;
    display: flex;
    list-style: none;
  }

  &__tab-with-logo {
    margin-right: 5px;
  }

  &__post-snippet-wrap {
    flex-grow: 1;
    padding: 12px 3px 13px;
    border: 1px #d7d7d7 solid;
    border-radius: 0 6px 6px 6px;
    background: white;
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.16);
  }

  &__post-snippet {
    margin-bottom: 19px;
  }

  &__post-snippet-textarea {
    scroll-behavior: smooth;
  }

  &__share-and-dislike-buttons {
    margin: 0 13px 6px;
  }
}

@mixin skeletonElement {
  border-radius: 3px;
  background: #e8e8e8;
  color: transparent;
}

.post-preview-section-skeleton {
  display: flex;
  align-items: stretch;

  .tab-with-logo {
    &__logo {
      @include skeletonElement;
      @include skeletonLoading;

      :deep(svg) {
        opacity: 0;
      }
    }

    &__text {
      @include skeletonElement;
      @include skeletonLoading;
    }
  }

  .photos-section {
    margin: 15px -9px 0;
    gap: 4px;
    display: flex;
    flex-wrap: wrap;

    &__photo {
      aspect-ratio: 1/1;
      @include skeletonElement;
      @include skeletonLoading;

      &--big {
        width: calc(50% - 4px / 2);
      }

      &--small {
        width: calc(33.3% - 4px * 2 / 3);
      }
    }
  }

  .buttons-panel {
    margin-top: 20px;
    display: flex;
    justify-content: space-between;

    &__button {
      height: 33px;
      @include skeletonElement;
      @include skeletonLoading;

      &--big {
        width: 170px;
      }

      &--small {
        width: 50px;
      }
    }
  }

  .post-preview-section {
    position: relative;
    z-index: 0;

    &__post-snippet-wrap {
      position: relative;
      z-index: -1;
    }

    &__slot-wrap {
      width: 100%;
      height: 100%;
      display: flex;
      justify-content: center;
      align-items: center;
      position: absolute;
      inset: 0 auto auto 0;
      z-index: 2;
    }

    &__snippet {
      padding: 0 10px;
      position: relative;
      overflow: hidden;
    }

    &__snippet-text {
      @include skeletonElement;
      @include skeletonLoading;
      box-decoration-break: clone;
      -webkit-box-decoration-break: clone;
      color: transparent;
      user-select: none;
    }
  }
}
</style>
